import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Button, Input, Modal, message } from "antd";
import cn from "classnames";
import dayjs from "dayjs";
import {
  GET_CRYPTO_PAYMENTS_SETTINGS,
  UPDATE_CRYPTO_PAYMENTS_SETTINGS,
} from "../../api/graphql/crypto";
import { useUser } from "../../context/UserContext";
import dialog from "../../styles/dialog.module.scss";
import styles from "./styles.module.scss";

/**
 * Off / Test / Live of the USDT top-ups to the Ledger. Test runs on the TRON
 * Nile testnet for the listed tester accounts only, so the whole flow can be
 * tried with test USDT before clients pay real money. Super admins switch it;
 * the receiving address comes from the server and is only shown.
 */

type Mode = "OFF" | "TEST" | "LIVE";

type Settings = {
  mode: Mode;
  testerEmails: string[];
  addressConfigured: boolean;
  address: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
};

const MODES: { value: Mode; label: string; text: string }[] = [
  {
    value: "OFF",
    label: "Off",
    text: "Clients can't start a crypto top-up. Payments already started still finish.",
  },
  {
    value: "TEST",
    label: "Test",
    text: "TRON Nile testnet, only for the tester accounts below. They pay with test USDT from the Nile faucet; their balance is credited and every screen marks it as a test.",
  },
  {
    value: "LIVE",
    label: "Live",
    text: "Mainnet, every client. Real USDT goes to the Ledger.",
  },
];

const MODE_TONE: Record<Mode, string> = {
  OFF: styles.toneNeutral,
  TEST: styles.toneWarning,
  LIVE: styles.toneSuccess,
};

const parseEmails = (text: string) =>
  text
    .split(/[\s,;]+/)
    .map((email) => email.trim())
    .filter(Boolean);

const ModeControl: React.FC = () => {
  const { user } = useUser();
  const canEdit = user?.role === "SUPER_ADMIN";
  const { data, refetch } = useQuery<{ cryptoPaymentsSettings: Settings }>(
    GET_CRYPTO_PAYMENTS_SETTINGS,
    { fetchPolicy: "cache-and-network" },
  );
  const [save, { loading: saving }] = useMutation(
    UPDATE_CRYPTO_PAYMENTS_SETTINGS,
  );
  const settings = data?.cryptoPaymentsSettings;
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("OFF");
  const [testers, setTesters] = useState("");

  useEffect(() => {
    if (!open || !settings) return;
    setMode(settings.mode);
    setTesters(settings.testerEmails.join("\n"));
  }, [open, settings]);

  if (!settings) return null;

  const current = MODES.find((option) => option.value === settings.mode);

  const handleSave = async () => {
    try {
      await save({
        variables: { mode, testerEmails: parseEmails(testers) },
      });
      await refetch();
      message.success(
        mode === settings.mode
          ? "Crypto payment settings saved"
          : `Crypto payments switched to ${
              MODES.find((o) => o.value === mode)?.label
            }`,
      );
      setOpen(false);
    } catch (error: any) {
      message.error(error?.message || "The settings were not saved");
    }
  };

  return (
    <span className={styles.modeControl}>
      <span
        className={cn(styles.badge, MODE_TONE[settings.mode])}
        title={
          settings.updatedBy
            ? `Set by ${settings.updatedBy}, ${dayjs(settings.updatedAt).format(
                "DD.MM.YYYY HH:mm",
              )}`
            : undefined
        }
      >
        Mode: {current?.label}
        {settings.mode === "TEST" && " · Nile testnet"}
      </span>
      {canEdit && (
        <button
          type="button"
          className={styles.action}
          onClick={() => setOpen(true)}
        >
          Change
        </button>
      )}

      {open && (
        <Modal
          open
          title={<span className={dialog.title}>Crypto payments mode</span>}
          onCancel={() => setOpen(false)}
          width={520}
          footer={[
            <div key="footer" className={dialog.footer}>
              <Button className={dialog.cancel} onClick={() => setOpen(false)}>
                Back
              </Button>
              <Button
                type="primary"
                loading={saving}
                disabled={
                  (mode !== "OFF" && !settings.addressConfigured) ||
                  (mode === "TEST" && parseEmails(testers).length === 0)
                }
                onClick={handleSave}
              >
                {mode === "LIVE" && settings.mode !== "LIVE"
                  ? "Switch to Live"
                  : "Save"}
              </Button>
            </div>,
          ]}
        >
          <p className={styles.modalText}>
            {settings.addressConfigured ? (
              <>
                Receiving address (set on the server):{" "}
                <span className={styles.mono}>{settings.address}</span>
              </>
            ) : (
              <span className={styles.warningText}>
                LEDGER_TRON_ADDRESS is not set on the server, so crypto payments
                can only stay off.
              </span>
            )}
          </p>

          <div className={styles.modes} role="radiogroup" aria-label="Mode">
            {MODES.map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={mode === option.value}
                className={cn(styles.modeOption, {
                  [styles.modeOptionActive]: mode === option.value,
                })}
                onClick={() => setMode(option.value)}
              >
                <span className={styles.modeLabel}>{option.label}</span>
                <span className={styles.modeText}>{option.text}</span>
              </button>
            ))}
          </div>

          {mode === "TEST" && (
            <>
              <label className={dialog.label} htmlFor="crypto-testers">
                Tester accounts
              </label>
              <Input.TextArea
                id="crypto-testers"
                value={testers}
                onChange={(event) => setTesters(event.target.value)}
                placeholder={"qa@app-installs.net\nanother@tester.com"}
                autoSize={{ minRows: 3, maxRows: 6 }}
              />
              <p className={styles.hint}>
                One email per line: the account a tester signs in to the
                platform with.
              </p>
            </>
          )}
        </Modal>
      )}
    </span>
  );
};

export default ModeControl;
